import { Component, OnInit, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';

import type { SearchOrderRow } from '../../core/api/api.types';
import { ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, PAYMENT_TYPE_LABELS, SERVICE_MODEL_LABELS } from '../../core/api/api.types';
import { OrdersApiService } from '../../core/api/orders-api.service';

type SortField = 'status' | 'updated_at' | 'city_code';
type SortDir = 'asc' | 'desc';

@Component({
  selector: 'op-search-page',
  imports: [DatePipe, FormsModule, RouterModule, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './search.page.html',
  styleUrl: './search.page.scss',
})
export class SearchPage implements OnInit {
  private readonly ordersApi = inject(OrdersApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  readonly orders = signal<SearchOrderRow[]>([]);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly selectedOrder = signal<SearchOrderRow | null>(null);

  searchQuery = '';
  sortField: SortField = 'updated_at';
  sortDir: SortDir = 'desc';

  async ngOnInit(): Promise<void> {
    const q = this.route.snapshot.queryParams;
    this.searchQuery = q['search'] || '';
    this.sortField = (q['sortBy'] as SortField) || 'updated_at';
    this.sortDir = (q['sortDir'] as SortDir) || 'desc';
    await this.loadOrders();
  }

  async onSearch(): Promise<void> {
    void this.writeUrl();
    await this.loadOrders();
  }

  async toggleSort(field: SortField): Promise<void> {
    if (this.sortField === field) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortDir = field === 'updated_at' ? 'desc' : 'asc';
    }
    void this.writeUrl();
    await this.loadOrders();
  }

  openDetail(order: SearchOrderRow): void {
    this.selectedOrder.set(order);
  }

  closeDetail(): void {
    this.selectedOrder.set(null);
  }

  // ── Display helpers ──────────────────────────────────────────────────────────

  statusLabel(status: string): string {
    return (ORDER_STATUS_LABELS as Record<string, string>)[status] ?? status;
  }

  statusTone(status: string): 'blue' | 'orange' | 'red' | 'green' {
    switch (status) {
      case 'finding_provider':
      case 'in_progress': return 'blue';
      case 'confirmed':
      case 'completed':
      case 'completed_externally':
      case 'referral_sent': return 'green';
      case 'closed':
      case 'cancelled': return 'red';
      default: return 'orange';
    }
  }

  providerLabel(order: SearchOrderRow): string {
    if (order.providers?.name) return order.providers.name;
    if (order.provider_assignment_type === 'unknown') return 'Unknown';
    if (order.provider_assignment_type === 'other_in_notes') return 'Other (in notes)';
    return '—';
  }

  customerName(order: SearchOrderRow): string {
    return order.handoff?.customer_preferred_name || '—';
  }

  serviceModelLabel(model: string): string {
    return (SERVICE_MODEL_LABELS as Record<string, string>)[model] ?? model;
  }

  paymentTypeLabel(type: string | null | undefined): string {
    if (!type) return '—';
    return (PAYMENT_TYPE_LABELS as Record<string, string>)[type] ?? type;
  }

  paymentStatusLabel(status: string | null | undefined): string {
    if (!status) return '—';
    return (PAYMENT_STATUS_LABELS as Record<string, string>)[status] ?? status;
  }

  centsToDollars(cents: number | null | undefined): string {
    if (!cents) return '—';
    return `$${(cents / 100).toFixed(2)}`;
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private async loadOrders(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      const res = await this.ordersApi.searchOrders({
        search:   this.searchQuery || undefined,
        statuses: ['closed'],
        sortBy:   this.sortField,
        sortDir:  this.sortDir,
      });
      this.orders.set(res.items);
      const sel = this.selectedOrder();
      if (sel) {
        this.selectedOrder.set(res.items.find(o => o.id === sel.id) ?? null);
      }
    } catch (err: any) {
      this.error.set(err?.message ?? 'Failed to load orders.');
    } finally {
      this.loading.set(false);
    }
  }

  private writeUrl(): Promise<boolean> {
    return this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        search:  this.searchQuery || null,
        sortBy:  this.sortField !== 'updated_at' ? this.sortField : null,
        sortDir: this.sortDir   !== 'desc'       ? this.sortDir   : null,
      },
      queryParamsHandling: 'replace',
      replaceUrl: true,
    });
  }
}
